import type { Bootstrap, MessageResponse, RuntimeConfig, TrackedEvent } from './types'

/**
 * Talks to the Laravel app.
 *
 * Token handling is the interesting part. The plugin's WordPress REST route mints tokens
 * server-side, and the widget fetches one at runtime rather than reading it out of the page,
 * so WordPress full-page caching can never serve a visitor an expired token. Tokens are
 * refreshed once on a 401 and the request retried; a second failure gives up quietly.
 */
export class Api {
  private token: string | null = null
  private tokenFetch: Promise<string | null> | null = null
  private sessionId: string | null = null

  constructor(private readonly config: RuntimeConfig) {
    this.token = config.token ?? null
  }

  private get base(): string {
    return this.config.apiUrl.replace(/\/+$/, '')
  }

  async bootstrap(): Promise<Bootstrap> {
    // Unauthenticated and cacheable — it must load before anything else can happen.
    const response = await fetch(`${this.base}/api/v1/widget/bootstrap`, {
      headers: { Accept: 'application/json' },
    })

    if (!response.ok) {
      throw new Error(`bootstrap failed: ${response.status}`)
    }

    return (await response.json()) as Bootstrap
  }

  /**
   * Resolved once and shared, so a burst of early calls doesn't mint several tokens.
   */
  private async getToken(): Promise<string | null> {
    if (this.token) {
      return this.token
    }

    if (!this.config.tokenUrl) {
      return null
    }

    this.tokenFetch ??= fetch(this.config.tokenUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { token?: string } | null) => {
        this.token = data?.token ?? null

        return this.token
      })
      .catch(() => null)
      .finally(() => {
        this.tokenFetch = null
      })

    return this.tokenFetch
  }

  private async send<T>(path: string, body: unknown, retry = true): Promise<T | null> {
    const token = await this.getToken()

    if (!token) {
      return null
    }

    let response: Response

    try {
      response = await fetch(`${this.base}/api/v1${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })
    } catch {
      // Offline or blocked. The caller shows its own fallback copy.
      return null
    }

    if (response.status === 401 && retry) {
      // The token expired mid-visit, which is expected on a long page view.
      this.token = null

      return this.send<T>(path, body, false)
    }

    if (!response.ok) {
      return null
    }

    return (await response.json()) as T
  }

  async openSession(): Promise<string | null> {
    const result = await this.send<{ session_id: string }>('/chat/session', {
      entry_url: window.location.href,
    })

    this.sessionId = result?.session_id ?? null

    return this.sessionId
  }

  async ask(message: string): Promise<MessageResponse | null> {
    if (!this.sessionId) {
      await this.openSession()
    }

    if (!this.sessionId) {
      return null
    }

    return this.send<MessageResponse>('/chat/message', {
      session_id: this.sessionId,
      message,
    })
  }

  async rate(rating: number, path: string): Promise<void> {
    if (!this.sessionId) {
      return
    }

    await this.send('/chat/rating', { session_id: this.sessionId, rating, path })
  }

  async sendEvents(events: TrackedEvent[]): Promise<void> {
    if (!this.sessionId || events.length === 0) {
      return
    }

    await this.send('/chat/events', { session_id: this.sessionId, events })
  }

  /**
   * Last-gasp flush on page unload. `sendBeacon` cannot set an Authorization header, so the
   * token rides in the body and the endpoint accepts either. Fire-and-forget by design —
   * there is no response to read and no chance to retry.
   */
  beaconEvents(events: TrackedEvent[]): void {
    if (!this.sessionId || !this.token || events.length === 0) {
      return
    }

    const payload = JSON.stringify({
      session_id: this.sessionId,
      events,
      token: this.token,
    })

    navigator.sendBeacon?.(
      `${this.base}/api/v1/chat/events`,
      new Blob([payload], { type: 'application/json' }),
    )
  }

  hasSession(): boolean {
    return this.sessionId !== null
  }
}
